import { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { BreadcrumbHeader, FilterTabs, EmptyState, ActionMenu, ConfirmModal } from '../../components/shared';
import { notificationRules as initialRules } from '../../services/dataClient';
import '../../styles/design-tokens.css';
import { fieldSx } from '../../theme/fieldStyles';

// ─── Constants ────────────────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";const TABS = [
{ label: 'All', value: 'all' },
{ label: 'Enabled', value: 'enabled' },
{ label: 'Disabled', value: 'disabled' }];

const EVENT_TYPES = ['SLA Breach', 'Low Stock', 'Shipment Delay', 'RMA Created'];
const CHANNELS = ['Email', 'In-App', 'Microsoft Teams'];

// True when the Microsoft Teams integration is connected (prototype constant)
const TEAMS_CONNECTED = true;

const EVENT_CONFIG = {
  'SLA Breach': { dot: '#B71C1C', desc: 'Triggers when a part order misses its SLA due date' },
  'Low Stock': { dot: '#E65100', desc: 'Triggers when inventory falls below the reorder threshold' },
  'Shipment Delay': { dot: '#1565C0', desc: 'Triggers when a shipment has not updated status within 48 hours' },
  'RMA Created': { dot: '#4527A0', desc: 'Triggers when a new return (RMA) is created' }
};

const CHANNEL_COLORS = {
  'Email': { bg: '#E3F2FD', color: '#1565C0' },
  'In-App': { bg: '#E0F2F1', color: '#00695C' },
  'Microsoft Teams': { bg: '#E3F2FD', color: '#1565C0' }
};

const POSITION_OPTIONS = ['Top Right', 'Bottom Right', 'Top Left', 'Bottom Left'];

const EMPTY_FORM = {
  eventType: 'SLA Breach',
  channels: ['Email'],
  recipients: [''],
  enabled: true
};

const DEFAULT_PREFS = {
  quietFrom: '22:00',
  quietTo: '07:00',
  position: 'Top Right',
  emailDigest: false,
  teamsFormat: 'Simple (plain text)'
};

const TEAMS_FORMAT_OPTIONS = ['Simple (plain text)', 'Detailed (includes part #, customer, ticket #)'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function relativeTime(dateStr) {
  if (!dateStr) return null;
  const diff = dayjs().diff(dayjs(dateStr), 'day');
  if (diff === 0) return 'today';
  if (diff === 1) return 'yesterday';
  if (diff < 30) return `${diff} days ago`;
  return dayjs(dateStr).format('MMM D, YYYY');
}

// Normalize mock data channel (string) → channels (array)
function normalize(r) {
  return {
    ...r,
    channels: Array.isArray(r.channels) ? r.channels : [r.channel].filter(Boolean)
  };
}

// ─── Channel badge ────────────────────────────────────────────────────────────
function ChannelBadge({ channel }) {
  const cfg = CHANNEL_COLORS[channel] ?? { bg: '#ECEFF1', color: '#546E7A' };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '2px', borderRadius: '10px', backgroundColor: cfg.bg }}>
      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: cfg.color, lineHeight: 1, whiteSpace: 'nowrap' }}>
        {channel}
      </Typography>
    </Box>);

}

// ─── Rule card ────────────────────────────────────────────────────────────────
function RuleCard({ rule, onToggle, onEdit, onTest, onDelete }) {
  const cfg = EVENT_CONFIG[rule.eventType] ?? { dot: '#607d8b', desc: '' };
  const triggered = relativeTime(rule.lastTriggered);

  // Truncate recipients
  const shown = rule.recipients.slice(0, 2);
  const extra = rule.recipients.length - 2;

  const actions = [
  { label: 'Edit', onClick: onEdit },
  { label: 'Test Notification', onClick: onTest },
  { label: 'Delete Rule', onClick: onDelete }];

  return (
    <Box
      sx={{ backgroundColor: 'var(--paper)',
        border: `1px solid ${rule.enabled ? 'var(--grey-200)' : 'var(--grey-200)'}`,
        borderRadius: '8px',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        opacity: rule.enabled ? 1 : 0.7
      }}>
      
      {/* Top row: dot + title | action menu + toggle */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', pt: '2px' }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: cfg.dot, flexShrink: 0, mt: '2px' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--grey-900)', lineHeight: 1.3 }}>
            {rule.eventType}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          <ActionMenu actions={actions} />
          <Switch
            size="small"
            checked={rule.enabled}
            onChange={() => onToggle(rule)}
            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#009688' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#009688' }
            }} />
          
        </Box>
      </Box>

      {/* Description */}
      <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', lineHeight: 1.5 }}>
        {cfg.desc}
      </Typography>

      {/* Recipients */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--grey-600)', flexShrink: 0 }}>
          Recipients:
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-700)' }}>
          {shown.join(', ')}
          {extra > 0 &&
          <Box component="span" sx={{ ml: '4px', color: '#009688', fontWeight: 600 }}>
              +{extra} more
            </Box>
          }
        </Typography>
      </Box>

      {/* Channels */}
      <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {rule.channels.map((ch) =>
        <ChannelBadge key={ch} channel={ch} />
        )}
      </Box>

      {/* Footer: last triggered */}
      <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)', mt: 'auto' }}>
        {triggered ? `Last triggered: ${triggered}` : 'Never triggered'}
      </Typography>
    </Box>);

}

// ─── Add / Edit modal ─────────────────────────────────────────────────────────
function RuleModal({ open, mode, initial, existingEventTypes, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_FORM);

  const handleEnter = () => {
    if (mode === 'edit' && initial) {
      setForm({
        eventType: initial.eventType,
        channels: [...initial.channels],
        recipients: initial.recipients.length ? [...initial.recipients] : [''],
        enabled: initial.enabled
      });
    } else {
      setForm(EMPTY_FORM);
    }
  };

  const toggleChannel = (ch) => {
    setForm((prev) => {
      const has = prev.channels.includes(ch);
      if (has && prev.channels.length === 1) return prev; // keep at least one
      return { ...prev, channels: has ? prev.channels.filter((c) => c !== ch) : [...prev.channels, ch] };
    });
  };

  const addRecipient = () => setForm((prev) => ({ ...prev, recipients: [...prev.recipients, ''] }));
  const removeRecipient = (i) => setForm((prev) => ({ ...prev, recipients: prev.recipients.filter((_, idx) => idx !== i) }));
  const setRecipient = (i, val) => setForm((prev) => {
    const r = [...prev.recipients];
    r[i] = val;
    return { ...prev, recipients: r };
  });

  const validRecipients = form.recipients.filter((r) => r.trim());
  const isDuplicate = mode === 'create' && existingEventTypes.includes(form.eventType);
  const isValid = form.eventType && form.channels.length > 0 && validRecipients.length > 0;

  const handleSave = () => onSave({ ...form, recipients: validRecipients });

  return (
    <Dialog open={open} onClose={(e, reason) => {if (reason !== 'backdropClick') onClose();}} maxWidth="sm" fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {mode === 'edit' ? 'Edit Notification Rule' : 'Add Notification Rule'}
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        {/* Event Type */}
        <FormControl size="small" required sx={fieldSx}>
          <InputLabel>Event Type</InputLabel>
          <Select
            label="Event Type"
            value={form.eventType}
            onChange={(e) => setForm((prev) => ({ ...prev, eventType: e.target.value }))}
            sx={{ fontSize: '0.875rem' }}>
            
            {EVENT_TYPES.map((t) =>
            <MenuItem key={t} value={t} sx={{ fontSize: '0.875rem' }}>{t}</MenuItem>
            )}
          </Select>
        </FormControl>

        {/* Duplicate warning */}
        {isDuplicate &&
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start', backgroundColor: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '6px', px: 2, py: '10px' }}>
            <WarningAmberIcon sx={{ fontSize: 16, color: '#E65100', mt: '1px', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.8125rem', color: '#E65100' }}>
              A rule for this event type already exists. Editing the existing rule is recommended.
            </Typography>
          </Box>
        }

        {/* Channel */}
        <Box>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-700)', mb: '6px' }}>
            Channel <Box component="span" sx={{ color: '#B71C1C' }}>*</Box>
          </Typography>
          <FormGroup row sx={{ gap: 1 }}>
            {CHANNELS.map((ch) =>
            <FormControlLabel
              key={ch}
              control={
              <Checkbox
                size="small"
                checked={form.channels.includes(ch)}
                onChange={() => toggleChannel(ch)}
                sx={{ color: 'var(--grey-400)', '&.Mui-checked': { color: '#009688' }, p: '4px' }} />

              }
              label={<Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>{ch}</Typography>} />

            )}
          </FormGroup>
        </Box>

        {/* Recipients */}
        <Box>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-700)', mb: '8px' }}>
            Recipients <Box component="span" sx={{ color: '#B71C1C' }}>*</Box>
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {form.recipients.map((email, idx) =>
            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                size="small"
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setRecipient(idx, e.target.value)}
                fullWidth
                sx={fieldSx} />
              
                {form.recipients.length > 1 &&
              <IconButton
                size="small"
                onClick={() => removeRecipient(idx)}
                sx={{ color: 'var(--grey-400)', '&:hover': { color: '#B71C1C' }, flexShrink: 0 }}>
                
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
              }
              </Box>
            )}
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              onClick={addRecipient}
              sx={{ alignSelf: 'flex-start',
                textTransform: 'none',
                fontSize: '0.8125rem',
                color: '#009688',
                p: 0,
                '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
              }}>
              
              Add recipient
            </Button>
          </Box>
        </Box>

        {/* Enabled toggle */}
        <FormControlLabel
          control={
          <Switch
            checked={form.enabled}
            onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
            size="small"
            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#009688' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#009688' }
            }} />

          }
          label={<Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>Enabled</Typography>} />
        
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          
          Cancel
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={!isValid}
          onClick={handleSave}
          sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2
          }}>
          
          {mode === 'edit' ? 'Save Changes' : 'Add Rule'}
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── Global preferences card ──────────────────────────────────────────────────
function GlobalPrefsCard({ onSave }) {
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const set = (field) => (e) => setPrefs((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <ContentCard sx={{ overflow: 'hidden' }}>
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid var(--grey-200)' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
          Global Preferences
        </Typography>
      </Box>

      <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Quiet Hours */}
        <Box>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-800)', mb: '4px' }}>
            Quiet Hours
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mb: 1.5 }}>
            Suppress non-critical notifications during these hours
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-600)', minWidth: 32 }}>From</Typography>
              <TextField
                size="small"
                type="time"
                value={prefs.quietFrom}
                onChange={set('quietFrom')}
                sx={{ width: 130, ...fieldSx }}
                inputProps={{ step: 300 }} />
              
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-600)', minWidth: 14 }}>To</Typography>
              <TextField
                size="small"
                type="time"
                value={prefs.quietTo}
                onChange={set('quietTo')}
                sx={{ width: 130, ...fieldSx }}
                inputProps={{ step: 300 }} />
              
            </Box>
          </Box>
        </Box>

        {/* In-App position */}
        <Box>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-800)', mb: 1 }}>
            In-App Notification Position
          </Typography>
          <FormControl size="small" sx={{ width: 200, ...fieldSx }}>
            <Select
              value={prefs.position}
              onChange={(e) => setPrefs((prev) => ({ ...prev, position: e.target.value }))}
              sx={{ fontSize: '0.875rem' }}>
              
              {POSITION_OPTIONS.map((p) =>
              <MenuItem key={p} value={p} sx={{ fontSize: '0.875rem' }}>{p}</MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>

        {/* Microsoft Teams message format */}
        {TEAMS_CONNECTED &&
        <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-800)', mb: 1 }}>
              Microsoft Teams Message Format
            </Typography>
            <FormControl size="small" sx={{ width: 320, ...fieldSx }}>
              <Select
              value={prefs.teamsFormat}
              onChange={(e) => setPrefs((prev) => ({ ...prev, teamsFormat: e.target.value }))}
              sx={{ fontSize: '0.875rem' }}>
              
                {TEAMS_FORMAT_OPTIONS.map((opt) =>
              <MenuItem key={opt} value={opt} sx={{ fontSize: '0.875rem' }}>{opt}</MenuItem>
              )}
              </Select>
            </FormControl>
          </Box>
        }

        {/* Email digest */}
        <FormControlLabel
          control={
          <Switch
            checked={prefs.emailDigest}
            onChange={(e) => setPrefs((prev) => ({ ...prev, emailDigest: e.target.checked }))}
            size="small"
            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#009688' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#009688' }
            }} />

          }
          label={
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>
              Send a daily digest email instead of individual emails for non-critical notifications
            </Typography>
          } />

        {/* Save button */}
        <Box>
          <Button
            variant="contained"
            size="small"
            onClick={() => onSave()}
            sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
              textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 3
            }}>
            
            Save Preferences
          </Button>
        </Box>
      </Box>
    </ContentCard>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function NotificationRulesPage() {
  const [rules, setRules] = useState(() => initialRules.map(normalize));
  const [activeTab, setActiveTab] = useState('all');
  const [banner, setBanner] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editTarget, setEditTarget] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState('delete'); // 'delete' | 'test'
  const [confirmTarget, setConfirmTarget] = useState(null);

  const showBanner = (msg) => {setBanner(msg);setTimeout(() => setBanner(''), 4000);};

  const filtered = useMemo(() => {
    return rules.filter((r) => {
      if (activeTab === 'enabled') return r.enabled;
      if (activeTab === 'disabled') return !r.enabled;
      return true;
    });
  }, [rules, activeTab]);

  // Existing event types for duplicate check (excludes the rule being edited)
  const existingEventTypes = useMemo(() =>
  rules
  .filter((r) => !editTarget || r.id !== editTarget.id)
  .map((r) => r.eventType),
  [rules, editTarget]
  );

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleToggle = (rule) => {
    setRules((prev) =>
    prev.map((r) => r.id === rule.id ? { ...r, enabled: !r.enabled } : r)
    );
    showBanner(rule.enabled ? 'Rule disabled' : 'Rule enabled');
  };

  const handleSave = (form) => {
    if (modalMode === 'edit') {
      setRules((prev) =>
      prev.map((r) => r.id === editTarget.id ? { ...r, ...form } : r)
      );
      showBanner('Notification rule updated successfully.');
    } else {
      setRules((prev) => [
      ...prev,
      {
        id: `nr-new-${Date.now()}`,
        code: `NR-${String(rules.length + 1).padStart(2, '0')}`,
        lastTriggered: null,
        ...form
      }]
      );
      showBanner('Notification rule created successfully.');
    }
    setModalOpen(false);
  };

  const handleDeleteConfirm = () => {
    setRules((prev) => prev.filter((r) => r.id !== confirmTarget.id));
    setConfirmOpen(false);
    showBanner('Notification rule deleted.');
    setConfirmTarget(null);
  };

  const handleTestConfirm = () => {
    const count = confirmTarget?.recipients?.length ?? 0;
    setConfirmOpen(false);
    showBanner(`Test notification sent to ${count} recipient${count !== 1 ? 's' : ''}.`);
    setConfirmTarget(null);
  };

  const openEdit = (rule) => {
    setEditTarget(rule);
    setModalMode('edit');
    setModalOpen(true);
  };

  const openTest = (rule) => {
    setConfirmType('test');
    setConfirmTarget(rule);
    setConfirmOpen(true);
  };

  const openDelete = (rule) => {
    setConfirmType('delete');
    setConfirmTarget(rule);
    setConfirmOpen(true);
  };

  // Confirm modal content
  const confirmTitle = confirmType === 'test' ? 'Send Test Notification' : 'Delete Rule';
  const confirmMessage = confirmType === 'test' ?
  `Send a test notification for ${confirmTarget?.eventType} to all recipients?` :
  `Are you sure you want to delete the ${confirmTarget?.eventType} rule? This cannot be undone.`;
  const confirmLabel = confirmType === 'test' ? 'Send Test' : 'Delete';
  const handleConfirm = confirmType === 'test' ? handleTestConfirm : handleDeleteConfirm;

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Success banner */}
      {banner &&
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7',
          borderRadius: '8px', px: 2, py: '12px'
        }}>
        
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            {banner}
          </Typography>
        </Box>
      }

      <BreadcrumbHeader
        title="Notifications"
        crumbs={[{ label: 'Settings' }, { label: 'Notifications' }]} />

      <ContentCard sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', '& > div:first-of-type': { mb: 0 } }}>
        <FilterTabs tabs={TABS} value={activeTab} onChange={setActiveTab} />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {setEditTarget(null);setModalMode('create');setModalOpen(true);}}
          sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
            textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none'
          }}>
          
          Add Rule
        </Button>
      </ContentCard>

      {/* Rules grid */}
      {filtered.length === 0 ?
      <ContentCard sx={{ overflow: 'hidden' }}>
          <EmptyState title="No notification rules found" description="Try adjusting your filter or add a new rule." />
        </ContentCard> :

      <Box
        sx={{ display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2
        }}>
        
          {filtered.map((rule) =>
        <RuleCard
          key={rule.id}
          rule={rule}
          onToggle={handleToggle}
          onEdit={() => openEdit(rule)}
          onTest={() => openTest(rule)}
          onDelete={() => openDelete(rule)} />

        )}
        </Box>
      }

      {/* Global Preferences */}
      <GlobalPrefsCard onSave={() => showBanner('Preferences saved successfully.')} />

      <RuleModal
        open={modalOpen}
        mode={modalMode}
        initial={editTarget}
        existingEventTypes={existingEventTypes}
        onClose={() => setModalOpen(false)}
        onSave={handleSave} />

      <ConfirmModal
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        confirmLabel={confirmLabel}
        onConfirm={handleConfirm}
        onCancel={() => {setConfirmOpen(false);setConfirmTarget(null);}} />
      
    </Box>);

}